import compact from "lodash/compact";
import isArray from "lodash/isArray";
import {
  FIELD_CUSTOM_FIELDS_KEY,
  FIELD_METADATA_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { TemplateKey } from "PFApp/constants/templates";
import { useVisiblePermittedProperties } from "PFApp/hooks/activity_form/use_visible_permitted_properties";
import { FormFieldValue } from "PFApp/types/activity_form/activity_form_schema";
import { activityCustomTypesAccessLevels } from "PFCore/helpers/custom_types";
import { useCustomTypes } from "PFCore/helpers/use_custom_types";
import { useCurrentProfile } from "PFCore/hooks/queries/profile";
import { AccessLevel, Importance, Profile, Subtemplate, Template } from "PFTypes";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";

type UsePropertyFieldValueChange = {
  template: Template;
  subtemplate: Subtemplate | null;
  activityCreator: Profile | null;
  roleFormKeyPrefix?: string;
};

export const usePropertyFieldValueChange = ({
  template,
  subtemplate,
  activityCreator,
  roleFormKeyPrefix = ""
}: UsePropertyFieldValueChange) => {
  const { data: currentProfile } = useCurrentProfile();
  const { getCustomTypeByName } = useCustomTypes();
  const { setValue } = useFormContext();

  const { getVisiblePermittedProperties } = useVisiblePermittedProperties();
  const visibleProperties = getVisiblePermittedProperties({ subtemplate, template, activityCreator });
  const fieldPrefix = roleFormKeyPrefix ? `${roleFormKeyPrefix}.` : "";

  return useCallback(
    (fieldName: string, values: FormFieldValue, bypassReadPermission?: boolean) => {
      const property = visibleProperties.find(({ name }) => name === fieldName);
      if (!property) {
        return;
      }
      const isCustomField = property.type === "custom_field";

      if (!isCustomField || !isArray(values)) {
        setValue(`${fieldPrefix}${FIELD_METADATA_KEY}.${property.name}`, values);
        return;
      }

      const customType = getCustomTypeByName(property.name);
      const accessLevel = customType
        ? activityCustomTypesAccessLevels(currentProfile, activityCreator)[customType.id]
        : undefined;
      if (
        !compact([AccessLevel.ReadWrite, bypassReadPermission && AccessLevel.ReadOnly]).includes(accessLevel)
      ) {
        return;
      }

      const isAuditRole = template.key === TemplateKey.AuditRole;

      const weightedValues = property.match
        ? values.map((value) => ({
            importance: isAuditRole ? Importance.Required : Importance.Supporting,
            ...value
          }))
        : values;

      setValue(`${fieldPrefix}${FIELD_CUSTOM_FIELDS_KEY}.${property.name}`, weightedValues);
    },
    [
      visibleProperties,
      currentProfile,
      activityCreator,
      template.key,
      fieldPrefix,
      getCustomTypeByName,
      setValue
    ]
  );
};
